import type {
  FaceData,
  OpticalMeasureFramingDistanceProfileSettings,
} from '../../index'
import type { PdMeasurementResult } from '../types'
import { useStableMeasurement } from './useStableMeasurement'

const pdVariance = (values: number[]) => {
  if (values.length < 2) return 999
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length
  return (
    values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length
  )
}

const averagePd = (samples: PdMeasurementResult[]): PdMeasurementResult => {
  const count = samples.length
  return {
    pd: samples.reduce((sum, sample) => sum + sample.pd, 0) / count,
    leftPd: samples.reduce((sum, sample) => sum + sample.leftPd, 0) / count,
    rightPd: samples.reduce((sum, sample) => sum + sample.rightPd, 0) / count,
    nearPd: samples.reduce((sum, sample) => sum + sample.nearPd, 0) / count,
    nearLeftPd:
      samples.reduce((sum, sample) => sum + sample.nearLeftPd, 0) / count,
    nearRightPd:
      samples.reduce((sum, sample) => sum + sample.nearRightPd, 0) / count,
  }
}

export function usePdMeasurement(
  distanceProfile: OpticalMeasureFramingDistanceProfileSettings,
  onComplete: (result: PdMeasurementResult) => void
) {
  return useStableMeasurement<PdMeasurementResult, PdMeasurementResult>({
    distanceProfile,
    onComplete,
    extractSample: (incoming: FaceData) => {
      if (
        incoming.nearPd == null ||
        incoming.nearLeftPd == null ||
        incoming.nearRightPd == null
      ) {
        return null
      }

      return {
        pd: incoming.pd,
        leftPd: incoming.leftPd,
        rightPd: incoming.rightPd,
        nearPd: incoming.nearPd,
        nearLeftPd: incoming.nearLeftPd,
        nearRightPd: incoming.nearRightPd,
      }
    },
    variance: (samples) => pdVariance(samples.map((sample) => sample.pd)),
    average: averagePd,
    bufferSize: 5,
    stableForMs: 500,
    sampleDurationMs: 500,
  })
}
